/**
 * ReportsPage — Three-tab reporting shell
 * Financial | Execution | Statistical
 */
import React, { useState } from "react";
import { FinancialReportTab } from "./reports/FinancialReportTab.js";
import { ExecutionReportTab } from "./reports/ExecutionReportTab.js";
import { StatisticalReportTab } from "./reports/StatisticalReportTab.js";

type ReportTab = "financial" | "execution" | "statistical";

const TABS: Array<{ id: ReportTab; label: string; icon: string }> = [
  { id: "financial", label: "Financial", icon: "💰" },
  { id: "execution", label: "Execution", icon: "⚙️" },
  { id: "statistical", label: "Statistical", icon: "📊" },
];

const page: React.CSSProperties = {
  padding: "32px 40px",
  maxWidth: 1400,
  margin: "0 auto",
  fontFamily: "var(--font-sans, 'Inter', sans-serif)",
};

const header: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 16,
  marginBottom: 28,
};

const title: React.CSSProperties = {
  fontSize: 24,
  fontWeight: 700,
  color: "var(--ink, #0f172a)",
  margin: 0,
};

const tabBar: React.CSSProperties = {
  display: "flex",
  gap: 4,
  background: "var(--panel, #f8fafc)",
  border: "1px solid var(--border, #e2e8f0)",
  borderRadius: "var(--radius, 8px)",
  padding: 4,
  marginBottom: 28,
  width: "fit-content",
};

function tabStyle(active: boolean): React.CSSProperties {
  return {
    padding: "8px 20px",
    borderRadius: 6,
    border: "none",
    cursor: "pointer",
    fontWeight: active ? 600 : 400,
    fontSize: 14,
    background: active ? "var(--brand, #6366f1)" : "transparent",
    color: active ? "#fff" : "var(--muted, #64748b)",
    transition: "all 0.15s ease",
  };
}

export function ReportsPage(): React.ReactElement {
  const [tab, setTab] = useState<ReportTab>("financial");

  return (
    <div style={page}>
      <div style={header}>
        <span style={{ fontSize: 28 }}>📈</span>
        <h1 style={title}>Reports</h1>
      </div>

      <div style={tabBar}>
        {TABS.map((t) => (
          <button
            key={t.id}
            id={`report-tab-${t.id}`}
            style={tabStyle(tab === t.id)}
            onClick={() => setTab(t.id)}
          >
            {t.icon} {t.label}
          </button>
        ))}
      </div>

      {tab === "financial" && <FinancialReportTab />}
      {tab === "execution" && <ExecutionReportTab />}
      {tab === "statistical" && <StatisticalReportTab />}
    </div>
  );
}
